import React, { useEffect, useState, useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import { useUpdateAppSettingsMutation, AppSettings } from '@SERVICES';
import { updatePrimaryButtonTextColor } from '@HELPERS/colorUtils';

import { notify } from '@NOTIFICATION/Notificator';

import BasicInput from '@COMPONENTS/COMMON/inputs/input/BasicInput';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import FileUploadDropzone from '@COMPONENTS/COMMON/inputs/file/FileUploadDropzone';

import styles from './AppSettingsTab.module.scss';

const cx: CX = classnames.bind(styles);

function AppSettingsTab(props: Props) {
    const { appSettingsData } = props;

    const [updateAppSettings, { isLoading: isUpdating }] = useUpdateAppSettingsMutation();

    // Form state for app settings
    const [formData, setFormData] = useState({
        app_name: appSettingsData.app_name || '',
        primary_color: appSettingsData.primary_color || '#00a4ba',
        secondary_color: appSettingsData.secondary_color || '#00a4ba',
        text_color: appSettingsData.text_color || '#00a4ba',
        admin_accent_color: appSettingsData.admin_accent_color || '#00a4ba',
    });

    const [logoFile, setLogoFile] = useState<File | null>(null);

    // Update form state when appSettingsData changes
    useEffect(() => {
        setFormData({
            app_name: appSettingsData.app_name || '',
            primary_color: appSettingsData.primary_color || '#00a4ba',
            secondary_color: appSettingsData.secondary_color || '#00a4ba',
            text_color: appSettingsData.text_color || '#00a4ba',
            admin_accent_color: appSettingsData.admin_accent_color || '#00a4ba',
        });
        
        // Force update button text color when app settings load
        if (appSettingsData.primary_color) {
            console.log('🔄 Forcing button text color update from AppSettingsTab');
            updatePrimaryButtonTextColor(appSettingsData.primary_color);
        }
    }, [appSettingsData]);

    const handleInputChange = (field: keyof typeof formData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleColorChange = useCallback((field: keyof typeof formData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        
        // Update button text color immediately when primary color changes
        if (field === 'primary_color') {
            console.log('🎨 Primary color changed to:', value);
            updatePrimaryButtonTextColor(value);
        }
    }, []);

    const handleLogoUpload = (files: File[]) => {
        if (files.length > 0) {
            setLogoFile(files[0]);
        }
    };

    const handleLogoClear = () => {
        setLogoFile(null);
    };

    const handleSubmit = async () => {
        try {
            const updateData: any = { ...formData };

            // Add logo file if uploaded
            if (logoFile) {
                updateData.header_logo_image = logoFile;
            }

            await updateAppSettings(updateData).unwrap();

            notify.success('App settings updated successfully!', {
                toastId: 'app-settings-success',
            });

            // Reload the page to apply the updated colors throughout the UI
            setTimeout(() => {
                window.location.reload();
            }, 1000); // Wait 1 second to show the success notification
        } catch (error) {
            notify.error('Failed to update app settings!', {
                toastId: 'app-settings-error',
            });
        }
    };

    return (
        <div className={cx('app-settings-tab')}>
            <div className={cx('form-section')}>
                <h2>Application Branding</h2>
                <p className={cx('section-description')}>
                    These settings control the appearance of the web application interface.
                </p>

                <div className={cx('form-group')}>
                    <FileUploadDropzone
                        label="Application Logo"
                        title="Click or drag logo image to upload"
                        onDrop={handleLogoUpload}
                        selectedFile={logoFile}
                        onClear={handleLogoClear}
                        accept={{
                            'image/jpeg': [],
                            'image/png': [],
                        }}
                    />
                    {!logoFile && appSettingsData.header_logo_image && (
                        <div className={cx('current-logo')}>
                            <p>
                                Current logo:
                                {' '}
                                {appSettingsData.header_logo_image}
                            </p>
                        </div>
                    )}
                </div>

                <div className={cx('form-group')}>
                    <BasicInput
                        value={formData.app_name}
                        onChange={(value) => handleInputChange('app_name', value)}
                        label="Application Name"
                        placeholder="Enter application name"
                    />
                </div>

                <div className={cx('app-colors-section')}>
                    <h3>Application Colors</h3>
                    <p className={cx('section-description')}>
                        These colors are used throughout the web application interface, including headers, buttons, and navigation elements.
                    </p>

                    <div className={cx('colors-grid')}>
                        <div className={cx('color-group')}>
                            <label htmlFor="primary_color">
                                <span className={cx('color-name')}>Primary Color</span>
                                <span className={cx('color-usage')}>Main brand color for headers and primary buttons</span>
                            </label>
                            <div className={cx('color-input-wrapper')}>
                                <input
                                    id="primary_color"
                                    type="color"
                                    value={formData.primary_color}
                                    onChange={(e) => handleColorChange('primary_color', e.target.value)}
                                    className={cx('color-input')}
                                />
                                <span className={cx('color-value')}>{formData.primary_color}</span>
                            </div>
                        </div>

                        <div className={cx('color-group')}>
                            <label htmlFor="secondary_color">
                                <span className={cx('color-name')}>Secondary Color</span>
                                <span className={cx('color-usage')}>Secondary brand color for accents</span>
                            </label>
                            <div className={cx('color-input-wrapper')}>
                                <input
                                    id="secondary_color"
                                    type="color"
                                    value={formData.secondary_color}
                                    onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                                    className={cx('color-input')}
                                />
                                <span className={cx('color-value')}>{formData.secondary_color}</span>
                            </div>
                        </div>

                        <div className={cx('color-group')}>
                            <label htmlFor="text_color">
                                <span className={cx('color-name')}>Text Color</span>
                                <span className={cx('color-usage')}>Main text color for UI elements</span>
                            </label>
                            <div className={cx('color-input-wrapper')}>
                                <input
                                    id="text_color"
                                    type="color"
                                    value={formData.text_color}
                                    onChange={(e) => handleColorChange('text_color', e.target.value)}
                                    className={cx('color-input')}
                                />
                                <span className={cx('color-value')}>{formData.text_color}</span>
                            </div>
                        </div>

                        <div className={cx('color-group')}>
                            <label htmlFor="admin_accent_color">
                                <span className={cx('color-name')}>Admin Accent Color</span>
                                <span className={cx('color-usage')}>Special color for admin interface elements</span>
                            </label>
                            <div className={cx('color-input-wrapper')}>
                                <input
                                    id="admin_accent_color"
                                    type="color"
                                    value={formData.admin_accent_color}
                                    onChange={(e) => handleColorChange('admin_accent_color', e.target.value)}
                                    className={cx('color-input')}
                                />
                                <span className={cx('color-value')}>{formData.admin_accent_color}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={cx('form-actions')}>
                    <BasicButton
                        onClick={handleSubmit}
                        isProcessing={isUpdating}
                        title="Save App Settings"
                        variant="primary"
                        size="medium"
                    />
                </div>
            </div>
        </div>
    );
}

AppSettingsTab.propTypes = {
    appSettingsData: PropTypes.shape({
        id: PropTypes.number,
        header_logo_image: PropTypes.string,
        app_name: PropTypes.string,
        primary_color: PropTypes.string.isRequired,
        secondary_color: PropTypes.string.isRequired,
        text_color: PropTypes.string.isRequired,
        admin_accent_color: PropTypes.string.isRequired,
    }).isRequired,
};

type Props = InferProps<typeof AppSettingsTab.propTypes>;

export default AppSettingsTab; 